import {
  Component,
  signal,
  ViewChild,
  ElementRef,
  inject,
  OnInit,
  OnDestroy,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GeminiService } from '../../services/gemini.service';
import {
  ReverbService,
  ReverbConnectionState,
} from '../../services/reverb.service';
import { CrudEmployeeService } from '../../gt2/content/crud/crud-employee/crud-employee.service';
import { Subscription } from 'rxjs';

interface ChatMessage {
  role: 'user' | 'model';
  text: string;
  isStreaming?: boolean;
  responseTime?: string;
  service?: 'Gemini' | 'Reverb';
}

@Component({
  selector: 'app-chat-widget',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  styleUrls: ['./chat-widget.tailwind.scss'],
  template: `
    <div class="fixed bottom-11 right-3 z-50 flex flex-col items-end gap-4">
      @if (isOpen()) {
        <div
          [style.width.px]="width()"
          [style.height.px]="height()"
          [class.transition-none]="isResizing()"
          [class.transition-all]="!isResizing()"
          class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden duration-300 ease-out border border-gray-100 dark:border-gray-700 animate-fade-in-up relative transition-colors"
        >
          <div
            class="absolute top-0 left-0 w-6 h-6 z-50 cursor-nw-resize flex items-start justify-start p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-br-lg transition-colors group"
            (mousedown)="startResize($event)"
            title="Resize chat"
          >
            <div
              class="w-2 h-2 border-t-2 border-l-2 border-gray-300 dark:border-gray-600 group-hover:border-indigo-400 dark:group-hover:border-indigo-400 rounded-tl-sm"
            ></div>
          </div>

          <div
            class="bg-indigo-600 dark:bg-indigo-700 p-4 flex justify-between items-center text-white shrink-0 select-none transition-colors"
            (dblclick)="resetSize()"
          >
            <div class="flex items-center gap-3 pl-4">
              <span class="material-symbols-outlined text-xl"
                >auto_awesome</span
              >
              <div class="flex items-center gap-2">
                <div class="relative group">
                  <select
                    [ngModel]="selectedService()"
                    (ngModelChange)="selectedService.set($event)"
                    class="appearance-none bg-indigo-700 dark:bg-indigo-800 border border-indigo-500 dark:border-indigo-600 text-white text-sm rounded pl-3 pr-8 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-400 cursor-pointer hover:bg-indigo-600 dark:hover:bg-indigo-700 transition-colors font-medium"
                  >
                    <option value="Gemini">Gemini</option>
                    <option value="Reverb">Reverb</option>
                  </select>
                </div>
                @if (selectedService() === 'Reverb') {
                  <div
                    class="w-3 h-3 rounded-full transition-all duration-300 border border-white/20"
                    [class]="reverbDotClass()"
                    [title]="'Reverb: ' + reverbService.connectionState()"
                  ></div>
                }
              </div>
            </div>
            <button
              (click)="toggleChat()"
              class="hover:bg-indigo-700 dark:hover:bg-indigo-600 p-1 rounded-full transition-colors focus:outline-none"
            >
              <span class="material-symbols-outlined text-sm">close</span>
            </button>
          </div>

          <div
            class="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900 chat-scroll transition-colors"
            #scrollContainer
          >
            @if (messages().length === 0) {
              <div
                class="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500 text-sm text-center px-6"
              >
                <span
                  class="material-symbols-outlined text-4xl mb-2 text-indigo-200 dark:text-indigo-800"
                  >auto_awesome</span
                >
                <p>{{ 'CHAT.EMPTY' | translate }}</p>
              </div>
            }

            @for (msg of messages(); track $index) {
              <div
                [class]="
                  msg.role === 'user'
                    ? 'flex justify-end'
                    : 'flex justify-start'
                "
              >
                <div
                  [class]="
                    msg.role === 'user'
                      ? 'bg-indigo-600 text-white rounded-2xl rounded-tr-sm px-4 py-2 max-w-[85%] shadow-sm'
                      : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-600 rounded-2xl rounded-tl-sm px-4 py-2 max-w-[85%] shadow-sm transition-colors relative group'
                  "
                >
                  <p
                    class="text-sm whitespace-pre-wrap leading-relaxed"
                    [innerText]="msg.text"
                  ></p>
                  @if (msg.role === 'model') {
                    <div
                      class="flex justify-between items-center mt-1 gap-2 border-t border-gray-100 dark:border-gray-600 pt-1"
                    >
                      <span
                        class="text-[10px] text-gray-400 dark:text-gray-500 font-mono flex items-center gap-1"
                      >
                        @if (msg.service === 'Reverb') {
                          <span class="material-symbols-outlined text-[10px]"
                            >wifi_tethering</span
                          >
                        } @else {
                          <span class="material-symbols-outlined text-[10px]"
                            >psychology</span
                          >
                        }
                        {{ msg.service || 'Gemini' }}
                      </span>
                      @if (msg.responseTime) {
                        <span
                          class="text-[10px] text-gray-400 dark:text-gray-500 font-mono"
                          >{{ msg.responseTime }}</span
                        >
                      }
                    </div>
                  }
                </div>
              </div>
            }

            @if (isLoading()) {
              <div class="flex justify-start">
                <div
                  class="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex items-center gap-1 transition-colors"
                >
                  <div
                    class="w-2 h-2 bg-indigo-400 rounded-full animate-bounce"
                  ></div>
                  <div
                    class="w-2 h-2 bg-indigo-400 rounded-full animate-bounce delay-75"
                  ></div>
                  <div
                    class="w-2 h-2 bg-indigo-400 rounded-full animate-bounce delay-150"
                  ></div>
                </div>
              </div>
            }
          </div>

          <div
            class="p-4 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 shrink-0 transition-colors"
          >
            <form (ngSubmit)="sendMessage()" class="flex gap-3 items-center">
              <div
                class="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full px-5 py-3 transition-colors"
              >
                <input
                  [(ngModel)]="currentInput"
                  name="chatInput"
                  type="text"
                  [placeholder]="'CHAT.PLACEHOLDER' | translate"
                  class="w-full bg-transparent border-none focus:ring-0 focus:outline-none p-0 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                  autocomplete="off"
                  [disabled]="isLoading() && selectedService() === 'Gemini'"
                />
              </div>
              <button
                type="submit"
                [disabled]="
                  !currentInput ||
                  (isLoading() && selectedService() === 'Gemini')
                "
                style="width: 30px; height: 30px;"
                class="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:scale-100 disabled:shadow-none shrink-0"
              >
                <span class="material-symbols-outlined text-xl ml-0.5"
                  >send</span
                >
              </button>
            </form>
          </div>
        </div>
      }

      <button
        (click)="toggleChat()"
        class="h-14 w-14 rounded-full bg-indigo-600 text-white shadow-lg hover:bg-indigo-700 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-center justify-center focus:outline-none focus:ring-4 focus:ring-indigo-200 dark:focus:ring-indigo-900 relative"
        [style.border]="
          selectedService() !== 'Reverb'
            ? '3px solid transparent'
            : reverbBorderColor()
        "
      >
        <span
          class="material-symbols-outlined text-2xl transition-transform duration-300"
          [class.rotate-90]="isOpen()"
        >
          {{ isOpen() ? 'close' : 'auto_awesome' }}
        </span>
      </button>
    </div>
  `,
  styles: [
    `
      .animate-fade-in-up {
        animation: fadeInUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
      }
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(20px) scale(0.95);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
      .delay-75 {
        animation-delay: 75ms;
      }
      .delay-150 {
        animation-delay: 150ms;
      }
    `,
  ],
})
export class ChatWidgetComponent implements OnInit, OnDestroy {
  geminiService = inject(GeminiService);
  reverbService = inject(ReverbService);
  private employeeService = inject(CrudEmployeeService);
  private translate = inject(TranslateService);

  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;

  isOpen = signal(false);
  isLoading = signal(false);
  messages = signal<ChatMessage[]>([]);
  currentInput = '';
  selectedService = signal<'Gemini' | 'Reverb'>('Reverb');
  private reverbSub!: Subscription;
  private lastReverbUserMsgTime: number | null = null;
  private conversationId: string | null = localStorage.getItem(
    'ai_chat_conversation_id',
  );

  width = signal(350);
  height = signal(500);
  isResizing = signal(false);
  private resizeStart = { x: 0, y: 0, w: 0, h: 0 };
  private boundOnResize = this.onResize.bind(this);
  private boundStopResize = this.stopResize.bind(this);

  constructor() {
    effect(() => {
      this.messages();
      this.isLoading();
      this.isOpen();
      setTimeout(() => this.scrollToBottom(), 50);
    });

    this.reverbSub = this.reverbService.messages$.subscribe((msg) => {
      if (this.selectedService() !== 'Reverb') return;

      if (msg.streaming) {
        // Append delta to existing streaming message or create new one
        this.messages.update((msgs) => {
          const lastMsg = msgs[msgs.length - 1];
          if (lastMsg && lastMsg.role === 'model' && lastMsg.isStreaming) {
            const updated = [...msgs];
            updated[updated.length - 1] = {
              ...lastMsg,
              text: lastMsg.text + msg.text,
            };
            return updated;
          }
          return [
            ...msgs,
            {
              role: 'model',
              text: msg.text,
              service: 'Reverb',
              isStreaming: true,
            },
          ];
        });
      } else if (msg.done) {
        // Stream ended — finalize message
        let responseTime: string | undefined;
        if (this.lastReverbUserMsgTime) {
          const duration = (Date.now() - this.lastReverbUserMsgTime) / 1000;
          responseTime = duration.toFixed(1) + 's';
          this.lastReverbUserMsgTime = null;
        }
        this.messages.update((msgs) => {
          const updated = [...msgs];
          const lastMsg = updated[updated.length - 1];
          if (lastMsg && lastMsg.role === 'model' && lastMsg.isStreaming) {
            updated[updated.length - 1] = {
              ...lastMsg,
              isStreaming: false,
              responseTime,
            };
          }
          return updated;
        });
        this.isLoading.set(false);
      }
    });
  }

  ngOnInit(): void {
    this.tryConnectReverb();
    this.loadChatHistory();
  }

  private async loadChatHistory(): Promise<void> {
    try {
      const result = await this.reverbService.loadHistory(
        this.conversationId || undefined,
      );
      if (result.conversation_id && result.messages.length > 0) {
        this.conversationId = result.conversation_id;
        localStorage.setItem('ai_chat_conversation_id', result.conversation_id);
        this.messages.set(
          result.messages.map((msg) => ({
            role: msg.role as 'user' | 'model',
            text: msg.content,
            service: 'Reverb' as const,
          })),
        );
      }
    } catch (error) {
      console.error('[Chat] Failed to load history', error);
    }
  }

  reverbDotClass(): string {
    const state = this.reverbService.connectionState();
    switch (state) {
      case 'connected':
        return 'bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.8)]';
      case 'connecting':
      case 'error':
        return 'bg-orange-400 shadow-[0_0_8px_rgba(251,146,60,0.8)]';
      default:
        return 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)]';
    }
  }

  reverbBorderColor(): string {
    const state = this.reverbService.connectionState();
    switch (state) {
      case 'connected':
        return '3px solid rgb(74 222 128)';
      case 'connecting':
      case 'error':
        return '3px solid rgb(251 146 60)';
      default:
        return '3px solid rgb(239 68 68)';
    }
  }

  private reverbRetryCount = 0;

  private tryConnectReverb(): void {
    const state = this.reverbService.connectionState();
    console.log(
      '[Reverb Widget] tryConnectReverb() — current state:',
      state,
      '— retry:',
      this.reverbRetryCount,
    );
    if (state === 'connected' || state === 'connecting') return;
    const employee = this.employeeService.me;
    if (employee?.user?.uuid) {
      console.log(
        '[Reverb Widget] Employee found, connecting with user uuid:',
        employee.user.uuid,
      );
      this.reverbRetryCount = 0;
      this.reverbService.connectToChat(employee.user.uuid);
    } else {
      console.warn(
        '[Reverb Widget] Employee not available yet, me:',
        !!employee,
      );
      if (this.reverbRetryCount < 10) {
        this.reverbRetryCount++;
        setTimeout(() => this.tryConnectReverb(), 2000);
      } else {
        console.error(
          '[Reverb Widget] Gave up after 10 retries — employee never loaded',
        );
      }
    }
  }

  toggleChat(): void {
    this.isOpen.update((v) => !v);
    if (this.isOpen()) {
      this.tryConnectReverb();
    }
  }

  resetSize(): void {
    this.width.set(350);
    this.height.set(500);
  }

  startResize(event: MouseEvent): void {
    event.preventDefault();
    this.isResizing.set(true);
    this.resizeStart = {
      x: event.clientX,
      y: event.clientY,
      w: this.width(),
      h: this.height(),
    };
    document.addEventListener('mousemove', this.boundOnResize);
    document.addEventListener('mouseup', this.boundStopResize);
  }

  onResize(event: MouseEvent): void {
    if (!this.isResizing()) return;
    const deltaX = this.resizeStart.x - event.clientX;
    const deltaY = this.resizeStart.y - event.clientY;
    const newW = Math.max(300, Math.min(800, this.resizeStart.w + deltaX));
    const newH = Math.max(
      400,
      Math.min(window.innerHeight - 150, this.resizeStart.h + deltaY),
    );
    this.width.set(newW);
    this.height.set(newH);
  }

  stopResize(): void {
    this.isResizing.set(false);
    document.removeEventListener('mousemove', this.boundOnResize);
    document.removeEventListener('mouseup', this.boundStopResize);
  }

  ngOnDestroy(): void {
    this.stopResize();
    this.reverbSub?.unsubscribe();
    this.reverbService.disconnect();
  }

  async sendMessage(): Promise<void> {
    if (!this.currentInput.trim()) return;
    if (this.selectedService() === 'Gemini' && this.isLoading()) return;

    const userMsg = this.currentInput.trim();
    const service = this.selectedService();
    this.currentInput = '';

    if (service === 'Reverb') {
      this.tryConnectReverb();
      this.messages.update((msgs) => [
        ...msgs,
        { role: 'user', text: userMsg, service: 'Reverb' },
      ]);
      this.isLoading.set(true);
      this.lastReverbUserMsgTime = Date.now();
      try {
        const result = await this.reverbService.sendMessage(
          userMsg,
          this.conversationId || undefined,
        );
        this.conversationId = result.conversation_id;
        if (this.conversationId) {
          localStorage.setItem('ai_chat_conversation_id', this.conversationId);
        }
      } catch {
        this.isLoading.set(false);
      }
      return;
    }

    this.messages.update((msgs) => [...msgs, { role: 'user', text: userMsg }]);
    this.isLoading.set(true);
    const startTime = Date.now();

    try {
      const stream = await this.geminiService.sendMessage(userMsg);
      let fullResponse = '';
      this.messages.update((msgs) => [
        ...msgs,
        { role: 'model', text: '', isStreaming: true, service },
      ]);

      for await (const chunk of stream) {
        const chunkText = (chunk as { text?: string }).text;
        if (chunkText) {
          fullResponse += chunkText;
          this.messages.update((msgs) => {
            const newMsgs = [...msgs];
            const lastMsg = newMsgs[newMsgs.length - 1];
            if (lastMsg && lastMsg.role === 'model') {
              lastMsg.text = fullResponse;
            }
            return newMsgs;
          });
        }
      }

      const endTime = Date.now();
      const durationInSeconds =
        parseFloat(((endTime - startTime) / 1000).toFixed(1)) + 's';

      this.messages.update((msgs) => {
        const newMsgs = [...msgs];
        const lastMsg = newMsgs[newMsgs.length - 1];
        if (lastMsg && lastMsg.role === 'model') {
          lastMsg.isStreaming = false;
          lastMsg.responseTime = durationInSeconds;
        }
        return newMsgs;
      });
    } catch (error) {
      console.error('Error sending message:', error);
      const errorText = this.translate.instant('CHAT.ERROR');
      this.messages.update((msgs) => [
        ...msgs,
        { role: 'model', text: errorText, service },
      ]);
    } finally {
      this.isLoading.set(false);
    }
  }

  private scrollToBottom(): void {
    try {
      const el = this.scrollContainer?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    } catch {
      // ignore
    }
  }
}
